import React from 'react'

import {
  Alert,
  Box,
  Button,
  Divider,
  RadioButton,
  Tag,
  Text
} from '@factorialco/deprecated-design-system'
import {
  BankingCardLimitPeriodEnum,
  BankingCardLimitTypeEnum,
  BankingCardPaymentCategory,
  CardDetailFragment,
  CardPaymentCategoryFragment,
  ExpensesValueObjectsSpendingLimitIntervalEnum,
  UpdateCardLimitsDocument
} from 'generated/resources/graphql'

import MoneyField from 'modules/core/components/MoneyField'
import SelectField from 'modules/core/components/SelectField'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import app from 'modules/core/store/models/app'

import { isCanceled } from 'modules/banking/lib/cards/isCanceled'
import { isSingleUse } from 'modules/banking/lib/cards/isSingleUse'

import { SidebarSection } from 'modules/banking/components/CardSidepanel/_Content/_SidebarSection'

const periodOptions = (): {
  label: string
  value: BankingCardLimitPeriodEnum
}[] => [
  {
    value: 'daily',
    label: i18n.t('expenses_v2.cards.limit.intervals.daily')
  },
  {
    value: 'weekly',
    label: i18n.t('expenses_v2.cards.limit.intervals.weekly')
  },
  {
    value: 'monthly',
    label: i18n.t('expenses_v2.cards.limit.intervals.monthly')
  },
  {
    value: 'yearly',
    label: i18n.t('expenses_v2.cards.limit.intervals.yearly')
  }
]

const getPeriodLabel = (period: BankingCardLimitPeriodEnum) => {
  return {
    daily: i18n.t('shift_management.hours_information.contract_hours.per_day'),
    weekly: i18n.t('shift_management.hours_information.contract_hours.per_week'),
    monthly: i18n.t('shift_management.hours_information.contract_hours.per_month'),
    yearly: i18n.t('shift_management.hours_information.contract_hours.per_year')
  }[period]
}

const CategoryLabel = ({
  selectedCategory,
  categories
}: {
  selectedCategory: string
  categories: BankingCardPaymentCategory[]
}) => {
  return (
    <Tag leftItem="circle" type="viridian" showCircle>
      {categories.find((cat) => cat.id == selectedCategory)?.label}
    </Tag>
  )
}

interface GetCategoriesLabelProps {
  categoryLimitType: BankingCardLimitTypeEnum
}
const LabeledCategories = ({
  categoryLimitType,
  children
}: React.PropsWithChildren<GetCategoriesLabelProps>) => {
  if (!categoryLimitType)
    return i18n.t('banking.cards.card_controls.all_categories_allowed_description')

  switch (categoryLimitType) {
    case 'allow':
      return i18n.tView('banking.cards.card_controls.allowed_categories_description', {
        category_list: children
      })
    case 'block': {
      return i18n.tView('banking.cards.card_controls.blocked_categories_description', {
        category_list: children
      })
    }
  }
}

type UpdateCardLimitFormValues = {
  id: string
  period: BankingCardLimitPeriodEnum
  amountCents?: string
  categoryLimitType?: BankingCardLimitTypeEnum | null
  categories: string[]
}

const useChangeCardLimits = (card: CardDetailFragment) => {
  const [updateLimits, { loading: submitting }] = useMutation(UpdateCardLimitsDocument, {
    extractorFn: (data) => data.bankingMutations.updateBankingCardLimit
  })

  let amountCents
  if (card.cardLimit.limitForCurrentPeriod) {
    amountCents = (card.cardLimit.limitForCurrentPeriod / 100).toString()
  } else {
    amountCents = ''
  }

  const form = useForm<UpdateCardLimitFormValues>({
    defaultValues: {
      id: card.id + '',
      period: card.cardLimit.period || 'monthly',
      amountCents: amountCents,
      categories: card.cardLimit.categories || [],
      categoryLimitType: card.cardLimit.limitType
    },
    validate: (data) => {
      const errors: FormErrors<UpdateCardLimitFormValues> = {}

      if (data.period && !data.amountCents) {
        errors.amountCents = i18n.t('common.empty')
      }

      if (!!data.categoryLimitType && !data.categories.length) {
        errors.categories = [i18n.t('banking.cards.card_controls.select_category')]
      }
      return errors
    },
    onSubmit: async (values) => {
      let newAmountCents
      if (values.amountCents) {
        newAmountCents = toCents(Number(values.amountCents))
      }
      const result = await updateLimits({
        variables: {
          id: values.id,
          period: values.period,
          amountCents: newAmountCents,
          categories: values.categories,
          limitType: values.categoryLimitType
        }
      })
      const handled = handleResult(result, (errors) => {
        form.setErrors({
          amountCents: errors['amountCents']
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully')
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving')
        })
      }
      return handled
    }
  })

  const submitDisabled = !form.dirty || submitting || form.isSubmitting || !form.isValid
  const onSubmit = () => form.handleSubmit()
  const onPeriodChange = (value: string) =>
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    form.setFieldValue('period', value as ExpensesValueObjectsSpendingLimitIntervalEnum)
  const onAmountCentsChange = (value: string) => form.setFieldValue('amountCents', value)
  const onCategoriesChange = (value: unknown) =>
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    form.setFieldValue('categories', value)

  return {
    form,
    submitDisabled,
    onSubmit,
    onPeriodChange,
    onAmountCentsChange,
    onCategoriesChange
  }
}

const ReadOnlyContent: React.FC<{
  card: CardDetailFragment
  skipCategories: boolean
  cardPaymentCategories: BankingCardPaymentCategory[]
}> = ({ card, cardPaymentCategories, skipCategories }) => {
  return (
    <Box gap="s12" width="full" paddingBottom="s12">
      <Box paddingY="s12">
        <Divider backgroundColor="black" />
      </Box>

      <Text.Normal weight="semibold" size="300" color="textPrimary">
        {i18n.t('banking.cards.card_controls.categories_control')}
      </Text.Normal>

      {isSingleUse(card) && (
        <Alert color="note">
          <Text.Normal color="grey1000">
            {i18n.t('expenses_v2.alerts.cards.edit_cards.cant_edit_for_single_use')}
          </Text.Normal>
        </Alert>
      )}
      {card.cardLimit.limitType && card.cardLimit.categories && !skipCategories && (
        <Box>
          <Text.Normal size="300" color="textSecondary">
            <Box gap="s16">
              <LabeledCategories categoryLimitType={card.cardLimit.limitType}>
                <Box
                  component="span"
                  inline
                  flexWrap="wrap"
                  flexDirection="row"
                  gap="s4"
                  paddingY="s4"
                >
                  {card.cardLimit.categories.map((selectedCategory) => (
                    <CategoryLabel
                      categories={cardPaymentCategories}
                      key={selectedCategory}
                      selectedCategory={selectedCategory}
                    />
                  ))}
                </Box>
              </LabeledCategories>
              {card.cardLimit.limitType == 'allow' &&
                i18n.t('banking.cards.card_controls.other_purchases_blocked')}
              {card.cardLimit.limitType == 'block' &&
                i18n.t('banking.cards.card_controls.other_purchases_allowed')}
            </Box>
          </Text.Normal>
        </Box>
      )}
    </Box>
  )
}

const EditableContent: React.FC<{
  card: CardDetailFragment
  skipCategories: boolean
  cardPaymentCategories: CardPaymentCategoryFragment[]
}> = ({ card, cardPaymentCategories, skipCategories }) => {
  const currency = card.cardLimit.currency

  const {
    form,
    submitDisabled,
    onSubmit,
    onPeriodChange,
    onAmountCentsChange,
    onCategoriesChange
  } = useChangeCardLimits(card)

  const updateLimitType = (limitType: BankingCardLimitTypeEnum | null) => {
    onCategoriesChange([])
    form.setFieldValue('categoryLimitType', limitType)
  }

  return (
    <Box flexDirection="column" gap="s32" width="full">
      <Box gap="s12" width="full" flexDirection="row" paddingBottom="s12">
        <MoneyField
          mandatory
          placeholder="0"
          currency={currency}
          label={i18n.t('banking.cards.card_controls.limit')}
          value={form.values.amountCents}
          onChange={onAmountCentsChange}
          error={form.errors.amountCents}
        />

        <SelectField
          mandatory
          label={i18n.t('banking.cards.card_controls.period')}
          options={periodOptions()}
          value={form.values.period}
          onChange={onPeriodChange}
        />
      </Box>

      <Divider backgroundColor="black" />

      <Box width="full" paddingBottom="s4" gap="s8">
        <Text.Normal weight="semibold" size="300" color="textPrimary">
          {i18n.t('banking.cards.card_controls.categories_control')}
        </Text.Normal>
        <Text.Normal weight="normal" size="300" color="textSecondary">
          {i18n.t('banking.cards.card_controls.categories_control_description')}
        </Text.Normal>

        <Box gap="s8" paddingY="s8">
          <RadioButton
            label={i18n.t('banking.cards.card_controls.allow_all_categories')}
            value=""
            checked={!form.values.categoryLimitType}
            onChange={() => {
              updateLimitType(null)
            }}
          />
          <RadioButton
            label={i18n.t('banking.cards.card_controls.allow_specific_categories')}
            value="allow"
            checked={form.values.categoryLimitType == 'allow'}
            onChange={() => {
              updateLimitType('allow')
            }}
          />
          <RadioButton
            label={i18n.t('banking.cards.card_controls.block_specific_categories')}
            value="block"
            checked={form.values.categoryLimitType == 'block'}
            onChange={() => {
              updateLimitType('block')
            }}
          />
        </Box>

        {!skipCategories &&
          (form.values.categoryLimitType == 'block' ||
            form.values.categoryLimitType == 'allow') && (
            <Selector.Popover
              aria-label={i18n.t('banking.cards.card_controls.category')}
              headerLabel={i18n.t('expenses_v2.cards.limit.categories.all_categories')}
              placeholder={i18n.t('expenses_v2.cards.limit.categories.add_one_or_more')}
              blankLabel={i18n.t('expenses_v2.cards.limit.categories.add_one_or_more')}
              onChange={onCategoriesChange}
              value={form.values.categories}
              searchPlaceholder={i18n.t('common.search')}
              allSelectedLabel={i18n.t('expenses_v2.cards.limit.categories.all_categories')}
              error={form.errors.categories?.at(0)}
              isMultiSelect
            >
              {cardPaymentCategories.map((category) => (
                <Selector.Option
                  key={category.id}
                  value={category.id}
                  label={category.label}
                  searchValue={category.label}
                >
                  {category.label}
                </Selector.Option>
              ))}
            </Selector.Popover>
          )}

        {form.values.categoryLimitType && !!form.values.categories.length && (
          <Alert color="note">
            <Text.Normal size="300" color="textSecondary">
              <Box gap="s16">
                <LabeledCategories categoryLimitType={form.values.categoryLimitType}>
                  <Box
                    component="span"
                    inline
                    flexWrap="wrap"
                    flexDirection="row"
                    gap="s4"
                    paddingY="s4"
                  >
                    {form.values.categories.map((selectedCategory) => (
                      <CategoryLabel
                        categories={cardPaymentCategories}
                        key={selectedCategory}
                        selectedCategory={selectedCategory}
                      />
                    ))}
                  </Box>
                </LabeledCategories>
                {form.values.categoryLimitType == 'allow' &&
                  i18n.t('banking.cards.card_controls.other_purchases_blocked')}
                {form.values.categoryLimitType == 'block' &&
                  i18n.t('banking.cards.card_controls.other_purchases_allowed')}
              </Box>
            </Text.Normal>
          </Alert>
        )}
      </Box>

      <Box width="full" flexDirection="rowReverse">
        <Button
          type="secondary"
          onClick={onSubmit}
          disabled={submitDisabled}
          aria-label={i18n.t('common.update')}
        >
          {i18n.t('common.update')}
        </Button>
      </Box>
    </Box>
  )
}

export const CardControls: React.FC<{
  card: CardDetailFragment
  cardPaymentCategories: CardPaymentCategoryFragment[]
  skipCategories: boolean
}> = ({ card, skipCategories, cardPaymentCategories }) => {
  const canEdit = card.cardLimit._policies?.update?.can && !isSingleUse(card) && !isCanceled(card)
  const {
    access: { locale }
  } = useCurrentSession()
  const currency = card.cardLimit.currency
  const periodText = isSingleUse(card)
    ? i18n.t('banking.cards.card_controls.periods.single_use')
    : card.cardLimit.period && getPeriodLabel(card.cardLimit.period)

  return (
    <SidebarSection>
      <Box width="full" paddingBottom="s4" gap="s8">
        <Text.Normal weight="semibold" size="300" color="textPrimary">
          {i18n.t('banking.cards.tabs.spending_limits.title')}
        </Text.Normal>

        {canEdit ? (
          <Text.Normal weight="normal" size="300" color="textSecondary">
            {i18n.t('banking.cards.tabs.spending_limits.description')}
          </Text.Normal>
        ) : (
          <Text.Normal weight="normal" size="300" color="textSecondary">
            {card.cardLimit.limitForCurrentPeriod &&
              MoneyWithCurrency({
                optionalDecimals: true,
                amount: card.cardLimit.limitForCurrentPeriod,
                currency,
                locale
              })}{' '}
            {periodText}
          </Text.Normal>
        )}
      </Box>

      {canEdit && (
        <EditableContent
          cardPaymentCategories={cardPaymentCategories}
          card={card}
          skipCategories={skipCategories}
        />
      )}

      {!canEdit && card.cardLimit.limitType && (
        <ReadOnlyContent
          cardPaymentCategories={cardPaymentCategories}
          card={card}
          skipCategories={skipCategories}
        />
      )}
    </SidebarSection>
  )
}
